export interface ScopeUnit { id: string; name: string; type: string }
export interface ScopeProgram { id: string; name: string; unitIds: string[] }
export interface AccountGrant { subjectId: string; unitId: string; region: string; includeDescendants: boolean }
export interface ScopeRow { dimension: string; values: string[] }
export interface AccountScope { programId?: string; unitId?: string; region: string; includeDescendants?: boolean }

const unitChoice = (grant: AccountGrant) => grant.unitId === '*' && grant.includeDescendants
  ? '*' : `u:${grant.includeDescendants ? 'tree' : 'self'}:${grant.unitId}`;

/** Collapse only complete, existing program assignments. A partial/stream-only grant
 * remains an explicit unit choice so editing another assignment cannot widen it. */
export function assignmentRows(grants: AccountGrant[], programs: ScopeProgram[]): ScopeRow[] {
  return [...new Set(grants.map(grant => grant.region))].map(region => {
    const matching = grants.filter(grant => grant.region === region);
    const consumed = new Set<string>();
    const values: string[] = [];
    for (const program of programs) {
      if (program.unitIds.length && program.unitIds.every(id => matching.some(grant => grant.unitId === id && grant.includeDescendants))) {
        values.push(`p:${program.id}`);
        program.unitIds.forEach(id => consumed.add(id));
      }
    }
    for (const grant of matching) {
      if (!grant.includeDescendants || !consumed.has(grant.unitId)) values.push(unitChoice(grant));
    }
    return { dimension: region, values: [...new Set(values)] };
  });
}

export function assignmentOptions(programs: ScopeProgram[], units: ScopeUnit[], rows: ScopeRow[]) {
  const options = [{ id: '*', label: 'All Programs / Streams' }, ...programs.filter(program => program.unitIds.length)
    .map(program => ({ id: `p:${program.id}`, label: program.name }))];
  const catalogUnits = new Set(programs.flatMap(program => program.unitIds));
  for (const unit of units.filter(unit => ['program','service','function'].includes(unit.type) && !catalogUnits.has(unit.id))) {
    options.push({ id: `u:tree:${unit.id}`, label: `${unit.name} (stream)` });
  }
  for (const id of new Set(rows.flatMap(row => row.values).filter(id => id.startsWith('u:')))) {
    const [, kind, ...parts] = id.split(':');
    const unitId = parts.join(':');
    const name = units.find(unit => unit.id === unitId)?.name || unitId;
    if (!options.some(option => option.id === id)) options.push({ id, label: `${name} (${kind === 'self' ? 'stream only' : 'existing stream'})` });
  }
  return options;
}

export function assignmentScopes(rows: ScopeRow[]): AccountScope[] {
  return rows.flatMap(row => [...new Set(row.values)].map(id => {
    if (!row.dimension.trim()) throw new Error('Select a region for each assignment');
    if (id === '*') return { unitId: '*', region: row.dimension };
    if (id.startsWith('p:')) return { programId: id.slice(2), region: row.dimension };
    const match = /^u:(tree|self):(.+)$/.exec(id);
    if (!match) throw new Error('Select a valid program or stream');
    return { unitId: match[2], region: row.dimension, includeDescendants: match[1] === 'tree' };
  }));
}

export const sameAssignments = (left: ScopeRow[], right: ScopeRow[]) => {
  const keys = (rows: ScopeRow[]) => [...new Set(rows.flatMap(row => row.values.map(value => JSON.stringify([row.dimension, value]))))].sort();
  return JSON.stringify(keys(left)) === JSON.stringify(keys(right));
};
